import React, { useEffect, useState } from "react";
import styles from "./accValidation.module.css";
import { Pagination } from "antd";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import AccValidationModal from "../../components/AccValidationModal/AccValidationModal";
import axios from "axios";
import Config from "../../service/Config";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers";
import { Popover } from "@mui/material";

const AccValidation = ({ title, loading }) => {
  const API_BASE = Config.kyc_api_endpoint;
  const [serchFilter, setSerchFilter] = useState("");
  const [totalFromBackend, setTotalFromBackend] = useState(0);
  const [data, setData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [noData, setNoData] = useState(false);

  const [accDetailsTab, setAccDetailsTab] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null); // 👈 add
  const [refresh, setRefresh] = useState(false);

  const [filterOpen, setFilterOpen] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [startDateDraft, setStartDateDraft] = useState(null);
  const [endDateDraft, setEndDateDraft] = useState(null);
  const [fetching, setFetching] = useState(false);
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);

  const handleFilterClick = (event) => {
    setFilterAnchorEl(event.currentTarget); // anchor to button
    setFilterOpen(true);
  };

  const handleFilterClose = () => {
    setFilterOpen(false);
    setFilterAnchorEl(null);
  };

  useEffect(() => {
    let ignore = false;
    (async () => {
      setFetching(true);
      try {
        let token = localStorage.getItem("authToken");
        let parsedToken = token ? JSON.parse(token) : {};
        console.log("parsedToken", parsedToken);
        if (!parsedToken.IdToken || !parsedToken.AccessToken) {
          localStorage.removeItem("authToken");
          // navigate("/login");
        }
        const { data } = await axios.get(
          `${API_BASE}/bank-account/all?flat=1&page=${currentPage}&limit=${pageSize}`,
          {
            headers: {
              "X-API-Key": process.env.REACT_APP_CUPI_API_KEY,
              aws_cognito_id_token: parsedToken.IdToken,
              aws_cognito_access_token: parsedToken.AccessToken
            },
            timeout: 15000,
          },
        );

        const items = Array.isArray(data?.items) ? data.items : [];
        setTotalFromBackend(data?.total || 0);

        const rows = items.map((it) => ({
          reference_id: it.reference_id,
          company_id: it.company_id,
          company_name: it.company_name,
          bank_name: it.bank_name,
          ifsc: it.ifsc,
          acc_no: it.acc_no,
          acc_no_last4: it.acc_no_last4,
          beneficiary_name: it.beneficiary_name,
          verified: !!it.verified,
          status: it.status || (it.verified ? "verified" : "unverified"),
          created_at: it.created_at,
          date: it.created_at
            ? new Date(it.created_at).toLocaleDateString()
            : "—",
          comp_name: it.company_name || it.company_id,
          bene_name: it.beneficiary_name || "—",
          __raw: it,
        }));

        if (!ignore) {
          setData(rows);
          setNoData(rows.length === 0);
        }
      } catch (e) {
        console.error("bank-account/all fetch failed:", e?.message || e);
        if (!ignore) {
          setData([]);
          setNoData(true);
        }
      } finally {
        if (!ignore) setFetching(false);
      }
    })();

    return () => {
      ignore = true;
    };
  }, [API_BASE, refresh, currentPage, pageSize]);

  const filteredData = data.filter((row) => {
    const s = serchFilter.toLowerCase();

    // text filter
    const matchesText =
      (row.comp_name || "").toLowerCase().includes(s) ||
      (row.acc_no || "").toLowerCase().includes(s) ||
      (row.bene_name || "").toLowerCase().includes(s) ||
      (row.status || "").toLowerCase().includes(s);

    // date filter
    const createdDate = row.created_at ? new Date(row.created_at) : null;
    const matchesDate =
      (!startDate || (createdDate && createdDate >= startDate)) &&
      (!endDate || (createdDate && createdDate <= endDate));

    return matchesText && matchesDate;
  });

const totalFiltered = filteredData.length;

const currentPageData = filteredData
  .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
  .slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const handlePaginationChange = (page, pageSize) => {
    setCurrentPage(page);
    setPageSize(pageSize);
  };

  const handleExport = () => {
    if (!filteredData.length) {
      alert("No data to export");
      return;
    }

    // Prepare clean rows for Excel
    const exportRows = filteredData.map((row) => ({
      Date: row.date,
      "Company Name": row.comp_name,
      "Account No": row.acc_no,
      "Beneficiary Name": row.bene_name,
      Status: row.status,
    }));

    // Create worksheet & workbook
    const worksheet = XLSX.utils.json_to_sheet(exportRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Account Validation");

    // Export as Blob
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    // Trigger download
    saveAs(blob, `account_validation_${new Date().toISOString()}.xlsx`);
  };

  return (
    <>
      {fetching && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            background: "rgba(255,255,255,0.7)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 2100,
          }}
        >
          <CircularProgress size={60} sx={{ color: "#2348AB" }} />
        </div>
      )}

      <div className={styles.frameGroup}>
        <div className={styles.feeStatementDataTable}>
          <div className={styles.top}>
            <div className={styles.filterWrapper}>
              <div className={styles.filter}>
                <div className={styles.containertext}>
                  {title || "Account Validation Request"}
                </div>
              </div>

              <div className={styles.filter}>
                <button
                  className={styles.filterLinesParent}
                  onClick={handleFilterClick}
                >
                  <img
                    className={styles.filterLinesIcon}
                    alt=""
                    src="/filterlines.svg"
                  />
                  <div className={styles.labelText2}>Filter</div>
                </button>

                <button
                  className={styles.download01Parent}
                  onClick={handleExport}
                >
                  <img
                    className={styles.filterLinesIcon}
                    alt=""
                    src="/download01.svg"
                  />
                  <div className={styles.labelText2}>Export</div>
                </button>

                <button
                  className={styles.reLoadTable}
                  onClick={() => setRefresh(!refresh)}
                >
                  <img
                    className={styles.vectorIcon1}
                    alt=""
                    src="/vector7.svg"
                  />
                </button>
              </div>
            </div>
            <div className={styles.topChild} />
          </div>

          <table className={styles.table}>
            <thead className={styles.tableHead}>
              <tr className={styles.tableHeaderRow}>
                <th className={styles.tableHeaderRowCell}>
                  <div className={styles.head}>DATE</div>
                </th>
                <th className={styles.tableHeaderRowCell}>
                  <div className={styles.head}>COMPANY NAME</div>
                </th>
                <th className={styles.tableHeaderRowCell}>
                  <div className={styles.head}>ACCOUNT NO</div>
                </th>
                <th className={styles.tableHeaderRowCell}>
                  <div className={styles.head}>BENEFICIARY NAME</div>
                </th>
                <th className={styles.tableHeaderRowCell}>
                  <div className={styles.head}>STATUS</div>
                </th>
                <th className={styles.tableHeaderRowCell}>
                  <div className={styles.head}>ACTION</div>
                </th>
              </tr>
            </thead>

            <tbody className={styles.tableHead}>
              {currentPageData.map((detail, index) => (
                <tr
                  key={`${detail.reference_id || index}`}
                  className={styles.tableBodyrow}
                >
                  <td className={styles.tableBodydatabtn}>
                    <div className={styles.data12}>{detail.date}</div>
                  </td>
                  <td className={styles.tableBodydata}>
                    <div className={styles.data1}>{detail.comp_name}</div>
                  </td>
                  <td className={styles.tableBodydata}>
                    <div className={styles.data1}>{detail.acc_no}</div>
                  </td>
                  <td className={styles.tableBodydata}>
                    <div className={styles.data1}>{detail.bene_name}</div>
                  </td>
                  <td className={styles.tableBodydata}>
                    <div className={styles.data1}>{detail.status}</div>
                  </td>
                  <td className={styles.tableBodydata}>
                    <button
                      className={styles.button}
                      onClick={() => {
                        setSelectedRow(detail); // 👈 capture this row
                        setAccDetailsTab(true); // open modal
                      }}
                    >
                      <img alt="" src="/eye.svg" className={styles.btn_img} />
                      View More
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {noData && <div className={styles.noHistory}>No records found.</div>}

          <div className={noData ? styles.bottom1 : styles.bottom}>
            <Pagination
              current={currentPage}
              pageSize={pageSize}
              total={totalFromBackend}
              onChange={handlePaginationChange}
            />
          </div>
        </div>
      </div>

      <Popover
        open={filterOpen}
        anchorEl={filterAnchorEl}
        onClose={handleFilterClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        PaperProps={{
          sx: { p: 2, width: 380, borderRadius: 2 }, // adjust width as needed
        }}
      >
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
            <DatePicker
              label="Start Date"
              value={startDateDraft}
              onChange={(newValue) => setStartDateDraft(newValue)}
              slotProps={{ textField: { fullWidth: true } }}
            />
            <DatePicker
              label="End Date"
              value={endDateDraft}
              onChange={(newValue) => setEndDateDraft(newValue)}
              slotProps={{ textField: { fullWidth: true } }}
            />
          </Box>
        </LocalizationProvider>

        <Typography align="center" sx={{ fontWeight: 600, mb: 2 }}>
          {startDateDraft && endDateDraft
            ? `Showing data from ${startDateDraft.toLocaleDateString()} to ${endDateDraft.toLocaleDateString()}`
            : "Please select a date range"}
        </Typography>

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
          <Button onClick={handleFilterClose}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              setStartDate(startDateDraft);
              setEndDate(endDateDraft);
              handleFilterClose();
            }}
            sx={{ backgroundColor: "#2348AB" }}
          >
            Apply
          </Button>
        </Box>
      </Popover>

      {accDetailsTab && (
        <div
          className={styles.ViewUserHeader}
          onClick={() => {
            /* click outside overlay if you want to close here */
          }}
        >
          <AccValidationModal
            isVisible={accDetailsTab}
            row={selectedRow} // 👈 pass the specific row
            setRefreshData={setRefresh}
            onClose={() => {
              setAccDetailsTab(false);
              setSelectedRow(null); // reset selection on close
            }}
          />
        </div>
      )}
    </>
  );
};

export default AccValidation;
